import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';
import { format } from 'date-fns';
import { useDateTimeFormat } from '../preferences/DateTimeFormatContext';

/**
 * A single reusable "log-entry" card used across the site:
 *   - Dashboard: activity / history / upcoming events
 *   - Landing: upcoming events sidebar
 *   - CarDetail: activity / history / events by owner
 *   - Events page: list view
 *
 * Visual pattern: red date badge on the left (MONTH / DAY / TIME), content
 * column in the middle, optional photo thumbnail on the right.
 */
export interface LogCardProps {
  /** Any parseable date. Used for the badge and sort. */
  date: Date | string | number;
  /** Primary line (title / car nickname / event title). */
  title: ReactNode;
  /** Optional small chip shown next to the title. */
  chip?: string;
  /** Optional body text under the title. */
  description?: ReactNode;
  /** Optional location string — shown with a pin icon. */
  location?: string;
  /** Optional organizer / author name — shown with a person icon. */
  organizer?: string;
  /** Optional right-side photo thumbnail (hidden on xs). */
  photo?: string;
  /** Click handler — wraps content in CardActionArea when present. */
  onClick?: () => void;
  /**
   * Router link target — wraps content in CardActionArea with a RouterLink.
   * Preferred over onClick for navigation: gives real anchor semantics
   * (ctrl-click new tab, right-click menu, etc).
   */
  to?: string;
}

export function LogCard({
  date,
  title,
  chip,
  description,
  location,
  organizer,
  photo,
  onClick,
  to,
}: LogCardProps) {
  const { formatTime } = useDateTimeFormat();
  const d = date instanceof Date ? date : new Date(date);

  const content = (
    <Stack direction="row" spacing={0} sx={{ width: '100%' }}>
      {/* Red date badge */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 1.5,
          py: 1.5,
          textAlign: 'center',
          minWidth: 72,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 700, lineHeight: 1 }}
        >
          {format(d, 'MMM')}
        </Typography>
        <Typography variant="h5">{format(d, 'd')}</Typography>
        <Typography
          variant="caption"
          sx={{ display: 'block', fontWeight: 600, lineHeight: 1.2, opacity: 0.9 }}
        >
          {formatTime(d)}
        </Typography>
      </Box>

      {/* Main content */}
      <CardContent sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{ flexWrap: 'wrap', mb: description || location || organizer ? 0.5 : 0 }}
        >
          {typeof title === 'string' ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {title}
            </Typography>
          ) : (
            title
          )}
          {chip && <Chip size="small" label={chip} />}
        </Stack>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: location || organizer ? 0.5 : 0 }}>
            {description}
          </Typography>
        )}

        {location && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: organizer ? 0.25 : 0 }}>
            <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {location}
            </Typography>
          </Stack>
        )}

        {organizer && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {organizer}
            </Typography>
          </Stack>
        )}
      </CardContent>

      {/* Optional photo thumbnail */}
      {photo && (
        <CardMedia
          component="img"
          image={photo}
          alt=""
          sx={{
            width: { xs: 0, sm: 160 },
            display: { xs: 'none', sm: 'block' },
            objectFit: 'cover',
          }}
        />
      )}
    </Stack>
  );

  return (
    <Card>
      {to ? (
        <CardActionArea component={RouterLink} to={to} sx={{ p: 0 }}>
          {content}
        </CardActionArea>
      ) : onClick ? (
        <CardActionArea onClick={onClick} sx={{ p: 0 }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}
